import React, {
  FC,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useCookies } from 'react-cookie'
import { useTranslation } from 'react-i18next'

import BagIcon from '@Assets/SVG/Bag'
import HeartIcon from '@Assets/SVG/Heart'
import UserIcon from '@Assets/SVG/User'
import { Abundance } from '@Components/Badges/Abundance'
import { CountryDetectionModal } from '@Components/CountryDetectionModal'
import { LanguageToggle } from '@Components/Header/LanguageToggle'
import { IconWithLabel, SVGIcon } from '@Components/Icons'
import { Link } from '@Components/Link'
import { LoginModals } from '@Components/Login'
import { SearchInput } from '@Components/Search/Desktop/Input'
import { NavigationEvent } from '@Events/Navigation'
import { MetadataContext } from '@Globals/Metadata'
import { storeURL } from '@Globals/Store'
import { UserContext } from '@Globals/User'

import css from './index.styl'

interface MainBarProps {
  hide: boolean
}

const WEB_CRAWLERS_REGEX = /bot|google|baidu|bing|msn|duckduckbot|teoma|slurp|yandex/i

export const MainBar: FC<MainBarProps> = (
  {
    hide,
  }: MainBarProps,
) => {
  const { t } = useTranslation()
  const classes = [css.MainBar]
  if (hide) {
    classes.push(css.Hide)
  }

  const [cookies, setCookie] = useCookies()
  const [loginModalOpen, setLoginModalOpen] = useState(false)
  const [countryDetectionModalOpen, setCountryDetectionModalOpen] = useState(false)

  const { store } = useContext(MetadataContext)
  const country = store?.config?.countryCode

  const accountURL = storeURL('customer/account')
  const cartURL = storeURL('checkout/cart/')
  const homeURL = storeURL('')
  const wishListURL = storeURL('account/wishlist/')

  const { userData } = useContext(UserContext)

  useEffect(() => {
    if (!userData?.geoIPCountryCode) {
      return
    }

    // Check for crawlers
    if (window.navigator && WEB_CRAWLERS_REGEX.test(window.navigator.userAgent)) {
      return
    }

    const currentCookieValue = cookies.th_country
    if (country === currentCookieValue) {
      return
    }
    setCookie('th_country', country)

    if (country === userData?.geoIPCountryCode) {
      return
    }

    setCountryDetectionModalOpen(true)
  }, [userData?.geoIPCountryCode])

  return (
    <div className={classes.join(' ')}>
      {userData?.geoIPCountryCode && (
        <CountryDetectionModal
          geoIPCountryCode={userData.geoIPCountryCode}
          onHide={() => setCountryDetectionModalOpen(false)}
          show={countryDetectionModalOpen}
        />
      )}
      <div className={css.MainBarInner}>
        {/* Search */}
        <SearchInput />

        {/* Logo */}
        <Link
          className={css.Logo}
          event={new NavigationEvent({
            attributes: {
              dest_page_type: 'frontpages',
              dest_page_type_id: '',
              source_id: 'header',
            },
            eventName: 'nav_link_click',
          })}
          href={homeURL}
        >
          <img
            alt="Trendhim"
            src="https://res.cloudinary.com/trendhim/image/upload/v1570536495/frontend/trendhim-logo-with-text.svg"
          />
        </Link>

        {/* Right side */}
        <div className={css.RightSide}>
          {/* Language toggle */}
          <LanguageToggle />

          {/* User account */}
          {userData?.loggedIn && (
            <Link
              className={css.Icon}
              event={new NavigationEvent({
                attributes: {
                  dest_page_type: 'customer-area',
                  dest_page_type_id: '',
                  source_id: 'header',
                },
                eventName: 'nav_link_click',
              })}
              href={accountURL}
            >
              <IconWithLabel label={t('header:MY_ACCOUNT_LABEL')}>
                <SVGIcon icon={UserIcon} />
              </IconWithLabel>
            </Link>
          )}
          {!userData?.loggedIn && (
            <button
              className={`${css.Icon} ${css.Button}`}
              onClick={() => setLoginModalOpen(true)}
              type="button"
            >
              <IconWithLabel label={t('header:LOG_IN_LABEL')}>
                <SVGIcon icon={UserIcon} />
              </IconWithLabel>
            </button>
          )}

          {/* Wish list */}
          <Link
            className={css.Icon}
            event={new NavigationEvent({
              attributes: {
                dest_page_type: 'wish-list',
                dest_page_type_id: '',
                source_id: 'header',
              },
              eventName: 'nav_link_click',
            })}
            href={wishListURL}
          >
            <IconWithLabel label={t('header:WISH_LIST_LABEL')}>
              <Abundance count={userData?.wishList?.abundance}>
                <SVGIcon icon={HeartIcon} />
              </Abundance>
            </IconWithLabel>
          </Link>

          {/* Cart */}
          <Link
            className={css.Icon}
            event={new NavigationEvent({
              attributes: {
                dest_page_type: 'cart',
                dest_page_type_id: '',
                source_id: 'header',
              },
              eventName: 'nav_link_click',
            })}
            href={cartURL}
          >
            <IconWithLabel label={t('header:CART_SHORT_LABEL')}>
              <Abundance count={userData?.basket?.quantity}>
                <SVGIcon icon={BagIcon} />
              </Abundance>
            </IconWithLabel>
          </Link>
        </div>
      </div>
      <LoginModals
        onHide={() => setLoginModalOpen(false)}
        show={loginModalOpen}
        sourceID="header"
      />
    </div>
  )
}
